import React, { useState } from 'react';
import { Save, PlayCircle } from 'lucide-react';
import type {
  IntegrationProvider,
  IntegrationConfig,
  FiscalProviderSettings,
  PaymentProviderSettings,
  MessagingProviderSettings
} from '../../types/integrations';

interface IntegrationFormProps {
  provider: IntegrationProvider;
  initialData?: IntegrationConfig | null;
  onSave: (config: IntegrationConfig) => Promise<void>;
  onTest: (config: IntegrationConfig) => Promise<void>;
}

const IntegrationForm: React.FC<IntegrationFormProps> = ({
  provider,
  initialData,
  onSave,
  onTest
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const formData = new FormData(e.target as HTMLFormElement);
      const data: IntegrationConfig = {
        id: initialData?.id || crypto.randomUUID(),
        providerId: provider.id,
        status: initialData?.status || 'configuring',
        credentials: {},
        settings: {},
        createdAt: initialData?.createdAt || new Date(),
        updatedAt: new Date()
      };

      // Build credentials object
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('credential_')) {
          data.credentials[key.replace('credential_', '')] = value as string;
        }
      }

      // Build settings object based on provider type
      switch (provider.type) {
        case 'fiscal':
          data.settings = {
            environment: formData.get('environment'),
            validateSchema: formData.get('validateSchema') === 'true',
            timeout: parseInt(formData.get('timeout') as string)
          } as FiscalProviderSettings;
          break;

        case 'payment':
          data.settings = {
            environment: formData.get('environment'),
            autoCapture: formData.get('autoCapture') === 'true',
            webhookSecret: formData.get('webhookSecret'),
            defaultInstallments: parseInt(formData.get('defaultInstallments') as string)
          } as PaymentProviderSettings;
          break;

        case 'messaging':
          data.settings = {
            allowedHours: {
              start: formData.get('allowedHoursStart'),
              end: formData.get('allowedHoursEnd')
            },
            templates: []
          } as MessagingProviderSettings;
          break;
      }

      await onSave(data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    if (!initialData) return;
    setIsTesting(true);
    try {
      await onTest(initialData);
    } finally {
      setIsTesting(false);
    }
  };

  const renderProviderFields = () => {
    switch (provider.type) {
      case 'fiscal':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Ambiente
              </label>
              <select
                name="environment"
                defaultValue={initialData?.settings?.environment || 'testing'}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="testing">Homologação</option>
                <option value="production">Produção</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Certificado Digital
              </label>
              <input
                type="file"
                name="credential_certificate"
                accept=".pfx"
                className="mt-1 block w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Senha do Certificado
              </label>
              <input
                type="password"
                name="credential_password"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Timeout (segundos)
              </label>
              <input
                type="number"
                name="timeout"
                defaultValue={initialData?.settings?.timeout || 30}
                min="1"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="validateSchema"
                defaultChecked={initialData?.settings?.validateSchema}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Validar schema dos documentos
              </label>
            </div>
          </>
        );

      case 'payment':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Ambiente
              </label>
              <select
                name="environment"
                defaultValue={initialData?.settings?.environment || 'sandbox'}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Produção</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Client ID
              </label>
              <input
                type="text"
                name="credential_clientId"
                defaultValue={initialData?.credentials?.clientId}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Client Secret
              </label>
              <input
                type="password"
                name="credential_clientSecret"
                defaultValue={initialData?.credentials?.clientSecret}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Webhook Secret
              </label>
              <input
                type="text"
                name="webhookSecret"
                defaultValue={initialData?.settings?.webhookSecret}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Número Máximo de Parcelas
              </label>
              <input
                type="number"
                name="defaultInstallments"
                defaultValue={initialData?.settings?.defaultInstallments || 12}
                min="1"
                max="24"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="autoCapture"
                defaultChecked={initialData?.settings?.autoCapture}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Captura automática de pagamentos
              </label>
            </div>
          </>
        );

      case 'messaging':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Token de Acesso
              </label>
              <input
                type="password"
                name="credential_token"
                defaultValue={initialData?.credentials?.token}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Horário Permitido - Início
                </label>
                <input
                  type="time"
                  name="allowedHoursStart"
                  defaultValue={initialData?.settings?.allowedHours?.start || '08:00'}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Horário Permitido - Fim
                </label>
                <input
                  type="time"
                  name="allowedHoursEnd"
                  defaultValue={initialData?.settings?.allowedHours?.end || '20:00'}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold">
          Configurar {provider.name}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {provider.description}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-6">
          {renderProviderFields()}
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          {initialData && (
            <button
              type="button"
              onClick={handleTest}
              disabled={isTesting}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center"
            >
              <PlayCircle className="h-5 w-5 mr-2" />
              {isTesting ? 'Testando...' : 'Testar Conexão'}
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <Save className="h-5 w-5 mr-2" />
            {isLoading ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IntegrationForm;